import React from 'react';
import { FieldProps, FieldValidation } from '@rjsf/core';
import { FormControl, TextField, FormHelperText } from '@material-ui/core';
import Select from '@mui/material/Select';
import './Extensions.css';
import { useForm, UseFormRegisterReturn } from 'react-hook-form';
import { appThemeApiRef, useApi } from '@backstage/core-plugin-api';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';

type wizExceptionType = {
  name: string;
  applicationName: string;
  containerRegistryName: string;
  imageName: string;
  wizSecurityPolicy: string;
  otherSecurityPolicy: string;
  exemptionCategory: string;
  riskLvl: string;
  exemptionType: string;
  justification: string;
  duration: string;
  isInitialException: boolean;
};

type aquaExceptionType = {
  name: string;
  applicationName: string;
  containerRegistryName: string;
  imageName: string;
  aquaSecurityPolicy: string;
  exemptionCategory: string;
  justification: string;
  duration: string;
  isInitialException: boolean;
};

type cloudSecurityExceptionType = {
  name: string;
  subscriptionName: string;
  resourceGroup: string;
  resourceName: string;
  cloudSecurityPolicy: string;
  exemptionCategory: string;
  justification: string;
  duration: string;
};

type snykExceptionType = {
  snykOrgId: string;
  snykProjectId: string;
  linkToIssue: string;
  exemptionCategory: string;
  exemptionType: string;
  justification: string;
  duration: string;
};

export const securityPolicyArr: { label: string; value: string }[] = [
  {
    label: 'Default',
    value: 'default',
  },
  {
    label: 'Application Image Assurance',
    value: 'ApplicationImageAssurance',
  },
  {
    label: 'Superuser',
    value: 'superuser',
  },
  {
    label: 'Vulnerability Score',
    value: 'vulnerabilityScore',
  },
  {
    label: 'Sensitive Information',
    value: 'sensitiveInformation',
  },
];

export const raiseSecurityExceptionDocsLink =
  'https://devportal.us.acuitynext.io/docs/default/component/prodsec-docs/policies-procedures/raise-security-exemption/Raise-Security-Exception/';

export const wizSecurityPolicyGroups: {
  section: string;
  options: { label: string; value: string }[];
}[] = [
  {
    section: 'CI/CD Pipeline',
    options: [
      {
        label: 'Acuity-Malware-Policy',
        value: 'Acuity-Malware-Policy',
      },
      {
        label: 'Acuity-SenstiveData-Policy',
        value: 'Acuity-SenstiveData-Policy',
      },
      {
        label: 'Acuity-Vulnerability-Policy',
        value: 'Acuity-Vulnerability-Policy',
      },
      {
        label: 'Acuity-Secrets-Policy',
        value: 'Acuity-Secrets-Policy',
      },
      {
        label: 'Others (not included above)',
        value: 'others-cicd',
      },
    ],
  },
  {
    section: 'RunTime Policies',
    options: [
      {
        label: 'BaseRunTime',
        value: 'BaseRunTime',
      },
      {
        label: 'Privileged_Containers',
        value: 'Privileged_Containers',
      },
      {
        label: 'Others (not included above)',
        value: 'others-runtime',
      },
    ],
  },
];

/*
 Both 'Others' entries (one per policy section) ask the requester to type the
 exact policy name, so they are resolved the same way by the form and the
 scaffolder action that writes the exemption yaml.
*/
export const isOtherWizSecurityPolicy = (securityPolicy?: string) =>
  securityPolicy === 'others-cicd' || securityPolicy === 'others-runtime';

export const appSecArchSecExceptionTypes = [
  'App Security Exception',
  'Architect Security Exception',
  'AppSec Risk Exception',
  'ArchSec Risk Exception',
];

export const riskArr: { label: string; value: string }[] = [
  {
    label: 'Critical',
    value: 'Critical',
  },
  {
    label: 'High',
    value: 'High',
  },
  {
    label: 'Medium',
    value: 'Medium',
  },
  {
    label: 'Low',
    value: 'Low',
  },
];

export const exemptionCategoryArr: { label: string; value: string }[] = [
  {
    label: 'Business Interruption',
    value: 'businessInterruption',
  },
  {
    label: 'Disruption to Critical Operation',
    value: 'disruptionToCriticalOperation',
  },
  {
    label: 'Legacy System',
    value: 'legacySystem',
  },
  {
    label: 'Ir-resolvable Exception',
    value: 'ir-resolvableException',
  },
];

export const exemptionTypeArr: { label: string; value: string }[] = [
  {
    label: 'Permanent',
    value: 'permanent',
  },
  {
    label: 'Temporary',
    value: 'temporary',
  },
];

export const formTextField = (
  registerCall: any,
  styling: any,
  label: string,
  helperText: string,
) => {
  return (
    <TextField
      style={styling}
      required
      label={label}
      helperText={helperText}
      {...registerCall}
    />
  );
};

export const formNumberField = (
  registerCall: any,
  styling: object,
  label: string,
  helperText: string,
  inputprops: object,
) => {
  return (
    <TextField
      style={styling}
      type="number"
      required
      label={label}
      helperText={helperText}
      {...registerCall}
      InputProps={inputprops}
    />
  );
};

export const justificationField = (registerCall: any, styling: object) => {
  return (
    <TextField
      style={styling}
      label="Justification"
      multiline
      minRows={2}
      helperText="Please elaborate on why this exception needs to be raised; it must include a detailed justification. If this is an exemption for a vulnerability, it must include the CVE or risk identifier for which the exemption is being requested."
      {...registerCall}
    />
  );
};

export const durationField = (
  registerCall: any,
  styling: object,
  inputprops: object = {},
) => {
  return (
    <TextField
      style={styling}
      type="number"
      required
      label="Duration"
      helperText="Exemption Duration in Days"
      defaultValue={42}
      {...registerCall}
      inputProps={inputprops}
    />
  );
};

export const docsLinkHelperText = (
  before: string,
  linkLabel: string,
  after: string,
) => {
  return (
    <FormHelperText>
      {before}
      <a
        href={raiseSecurityExceptionDocsLink}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'inherit', textDecoration: 'underline' }}
      >
        {linkLabel}
      </a>
      {after}
    </FormHelperText>
  );
};

export const appsec_archsec = (
  projectNameAndEnv:
    | UseFormRegisterReturn<'fieldArray.projectNameAndEnv'>
    | UseFormRegisterReturn<`fieldArray.${number}.projectNameAndEnv`>,
  version:
    | UseFormRegisterReturn<'fieldArray.version'>
    | UseFormRegisterReturn<`fieldArray.${number}.version`>,

  desc:
    | UseFormRegisterReturn<'fieldArray.desc'>
    | UseFormRegisterReturn<`fieldArray.${number}.desc`>,
  adoWorkNumber:
    | UseFormRegisterReturn<'fieldArray.adoWorkNumber'>
    | UseFormRegisterReturn<`fieldArray.${number}.adoWorkNumber`>,
  riskLvl:
    | UseFormRegisterReturn<'fieldArray.riskLvl'>
    | UseFormRegisterReturn<`fieldArray.${number}.riskLvl`>,
  exemptionCategoryCall:
    | UseFormRegisterReturn<'fieldArray.exemptionCategory'>
    | UseFormRegisterReturn<`fieldArray.${number}.exemptionCategory`>,
  exemptionTypeCall:
    | UseFormRegisterReturn<'fieldArray.exemptionType'>
    | UseFormRegisterReturn<`fieldArray.${number}.exemptionType`>,
  justificationCall:
    | UseFormRegisterReturn<'fieldArray.justification'>
    | UseFormRegisterReturn<`fieldArray.${number}.justification`>,
  durationCall:
    | UseFormRegisterReturn<'fieldArray.duration'>
    | UseFormRegisterReturn<`fieldArray.${number}.duration`>,
  themeID: string | undefined,
) => {
  return (
    <FormControl>
      {formTextField(
        { ...projectNameAndEnv },
        { marginBottom: '20px' },
        'Project Name & Environment',
        'Please provide application/solution name along with environment (Eg- Dev/QA/Prod or all).',
      )}

      {formTextField(
        { ...version },
        { marginBottom: '20px' },
        'Version',
        'Please specify the exact version/release or leave blank if requesting permanent exception.',
      )}

      {formTextField(
        { ...desc },
        { marginBottom: '20px' },
        'Description of the Security Issue',
        'Provide a detailed description of the security vulnerability, including the nature of the issue and its potential impact on the application and the organization.',
      )}

      {formNumberField(
        { ...adoWorkNumber },
        { marginBottom: '20px' },
        'ADO Work Item Number',
        'Azure ADO Work Item Number / PBI /Bug',
        {},
      )}

      <FormHelperText>Risk Level</FormHelperText>

      <Select
        {...riskLvl}
        sx={{
          backgroundColor: themeID === 'dark-theme' ? '#424242' : 'white',
          color: themeID === 'dark-theme' ? 'white' : 'black',
        }}
      >
        {riskArr.map(risk => (
          <MenuItem value={risk.value}>{risk.label}</MenuItem>
        ))}
      </Select>

      <FormHelperText>
        Please ensure risk owner included above aligns with risk severity
        approval workflow described here: - Critical - GM/SVP High - VP Medium –
        Director Low - Manager
      </FormHelperText>

      <FormHelperText>Exemption Category</FormHelperText>

      <Select
        {...exemptionCategoryCall}
        sx={{
          backgroundColor: themeID === 'dark-theme' ? '#424242' : 'white',
          color: themeID === 'dark-theme' ? 'white' : 'black',
        }}
      >
        {securityPolicyArr.map(policy => (
          <MenuItem value={policy.value}>{policy.label}</MenuItem>
        ))}
      </Select>

      <div style={{ marginBottom: '20px' }} />

      <FormHelperText>Exemption Type</FormHelperText>

      <Select
        {...exemptionTypeCall}
        sx={{
          backgroundColor: themeID === 'dark-theme' ? '#424242' : 'white',
          color: themeID === 'dark-theme' ? 'white' : 'black',
        }}
      >
        {exemptionTypeArr.map(policy => (
          <MenuItem value={policy.value}>{policy.label}</MenuItem>
        ))}
      </Select>

      <div style={{ marginBottom: '20px' }} />

      {justificationField(justificationCall, { marginBottom: '20px' })}

      <div style={{ marginBottom: '40px' }} />

      {durationField(durationCall, { marginBottom: '20px' })}
    </FormControl>
  );
};

export const cloudSecurityForm = (
  subscriptionNameCall:
    | UseFormRegisterReturn<'fieldArray.subscriptionName'>
    | UseFormRegisterReturn<`fieldArray.${number}.subscriptionName`>,
  resourceGroupCall:
    | UseFormRegisterReturn<'fieldArray.resourceGroup'>
    | UseFormRegisterReturn<`fieldArray.${number}.resourceGroup`>,
  resourceNameCall:
    | UseFormRegisterReturn<'fieldArray.resourceName'>
    | UseFormRegisterReturn<`fieldArray.${number}.resourceName`>,
  cloudSecurityPolicyCall:
    | UseFormRegisterReturn<'fieldArray.cloudSecurityPolicy'>
    | UseFormRegisterReturn<`fieldArray.${number}.cloudSecurityPolicy`>,
  exemptionCategoryCall:
    | UseFormRegisterReturn<'fieldArray.exemptionCategory'>
    | UseFormRegisterReturn<`fieldArray.${number}.exemptionCategory`>,
  exemptionTypeCall:
    | UseFormRegisterReturn<'fieldArray.exemptionType'>
    | UseFormRegisterReturn<`fieldArray.${number}.exemptionType`>,
  justificationCall:
    | UseFormRegisterReturn<'fieldArray.justification'>
    | UseFormRegisterReturn<`fieldArray.${number}.justification`>,
  durationCall:
    | UseFormRegisterReturn<'fieldArray.duration'>
    | UseFormRegisterReturn<`fieldArray.${number}.duration`>,
  themeID: string | undefined,
) => {
  return (
    <FormControl>
      {formTextField(
        { ...subscriptionNameCall },
        { marginBottom: '20px' },
        'Subscription Name',
        'Cloud Subscription name where the resource resides',
      )}

      {formTextField(
        { ...resourceGroupCall },
        { marginBottom: '20px' },
        'Resource Group',
        'Resource Group name where the resource resides',
      )}

      {formTextField(
        { ...resourceNameCall },
        { marginBottom: '20px' },
        'Resource Name',
        'Name of the resource',
      )}

      {formTextField(
        { ...cloudSecurityPolicyCall },
        { marginBottom: '20px' },
        'Cloud Security Policy',
        'Name of the cloud security policy',
      )}

      <Select
        {...exemptionCategoryCall}
        sx={{
          backgroundColor: themeID === 'dark-theme' ? '#424242' : 'white',
          color: themeID === 'dark-theme' ? 'white' : 'black',
        }}
      >
        {securityPolicyArr.map(policy => (
          <MenuItem value={policy.value}>{policy.label}</MenuItem>
        ))}
      </Select>
      <FormHelperText>Exemption Category</FormHelperText>

      <Select
        {...exemptionTypeCall}
        sx={{
          backgroundColor: themeID === 'dark-theme' ? '#424242' : 'white',
          color: themeID === 'dark-theme' ? 'white' : 'black',
        }}
      >
        {exemptionTypeArr.map(policy => (
          <MenuItem value={policy.value}>{policy.label}</MenuItem>
        ))}
      </Select>
      <FormHelperText>Exemption Type</FormHelperText>

      <div style={{ marginBottom: '40px' }} />

      {justificationField(justificationCall, { marginBottom: '20px' })}

      <div style={{ marginBottom: '40px' }} />

      {durationField(durationCall, { marginBottom: '20px' })}
    </FormControl>
  );
};

export const snykForm = (
  snykOrgIdCall:
    | UseFormRegisterReturn<'fieldArray.snykOrgId'>
    | UseFormRegisterReturn<`fieldArray.${number}.snykOrgId`>,
  snykProjectIdCall:
    | UseFormRegisterReturn<'fieldArray.snykProjectId'>
    | UseFormRegisterReturn<`fieldArray.${number}.snykProjectId`>,
  linkToIssueCall:
    | UseFormRegisterReturn<'fieldArray.linkToIssue'>
    | UseFormRegisterReturn<`fieldArray.${number}.linkToIssue`>,
  exemptionCategoryCall:
    | UseFormRegisterReturn<'fieldArray.exemptionCategory'>
    | UseFormRegisterReturn<`fieldArray.${number}.exemptionCategory`>,
  exemptionTypeCall:
    | UseFormRegisterReturn<'fieldArray.exemptionType'>
    | UseFormRegisterReturn<`fieldArray.${number}.exemptionType`>,
  justificationCall:
    | UseFormRegisterReturn<'fieldArray.justification'>
    | UseFormRegisterReturn<`fieldArray.${number}.justification`>,
  durationCall:
    | UseFormRegisterReturn<'fieldArray.duration'>
    | UseFormRegisterReturn<`fieldArray.${number}.duration`>,
  themeID: string | undefined,
) => {
  return (
    <FormControl>
      {formTextField(
        { ...snykOrgIdCall },
        { marginBottom: '20px' },
        'Subscription Name',
        'Cloud Subscription name where the resource resides',
      )}

      {formTextField(
        { ...snykProjectIdCall },
        { marginBottom: '20px' },
        'Resource Group',
        'Resource Group name where the resource resides',
      )}

      {formTextField(
        { ...linkToIssueCall },
        { marginBottom: '20px' },
        'Resource Name',
        'Name of the resource',
      )}

      <Select
        {...exemptionCategoryCall}
        sx={{
          backgroundColor: themeID === 'dark-theme' ? '#424242' : 'white',
          color: themeID === 'dark-theme' ? 'white' : 'black',
        }}
      >
        {securityPolicyArr.map(policy => (
          <MenuItem value={policy.value}>{policy.label}</MenuItem>
        ))}
      </Select>
      <FormHelperText>Exemption Category</FormHelperText>

      <Select
        {...exemptionTypeCall}
        sx={{
          backgroundColor: themeID === 'dark-theme' ? '#424242' : 'white',
          color: themeID === 'dark-theme' ? 'white' : 'black',
        }}
      >
        {exemptionTypeArr.map(policy => (
          <MenuItem value={policy.value}>{policy.label}</MenuItem>
        ))}
      </Select>
      <FormHelperText>Exemption Type</FormHelperText>

      <div style={{ marginBottom: '40px' }} />

      {justificationField(justificationCall, { marginBottom: '20px' })}

      <div style={{ marginBottom: '40px' }} />

      {durationField(durationCall, { marginBottom: '20px' })}
    </FormControl>
  );
};

export const aquaForm = (
  applicationNameCall:
    | UseFormRegisterReturn<'fieldArray.applicationName'>
    | UseFormRegisterReturn<`fieldArray.${number}.applicationName`>,
  containerRegistryNameCall:
    | UseFormRegisterReturn<'fieldArray.containerRegistryName'>
    | UseFormRegisterReturn<`fieldArray.${number}.containerRegistryName`>,
  imageNameCall:
    | UseFormRegisterReturn<'fieldArray.imageName'>
    | UseFormRegisterReturn<`fieldArray.${number}.imageName`>,
  aquaSecurityPolicyCall:
    | UseFormRegisterReturn<'fieldArray.aquaSecurityPolicy'>
    | UseFormRegisterReturn<`fieldArray.${number}.aquaSecurityPolicy`>,
  exemptionCategoryCal:
    | UseFormRegisterReturn<'fieldArray.exemptionCategory'>
    | UseFormRegisterReturn<`fieldArray.${number}.exemptionCategory`>,
  justificationCall:
    | UseFormRegisterReturn<'fieldArray.justification'>
    | UseFormRegisterReturn<`fieldArray.${number}.justification`>,
  durationCall:
    | UseFormRegisterReturn<'fieldArray.duration'>
    | UseFormRegisterReturn<`fieldArray.${number}.duration`>,
  themeID: string | undefined,
) => {
  return (
    <FormControl>
      {formTextField(
        { ...applicationNameCall },
        { marginBottom: '20px' },
        'Application Name',
        'Name of Aqua application',
      )}

      {formTextField(
        { ...containerRegistryNameCall },
        { marginBottom: '20px' },
        'Name of Container Reigstry',
        'Name of Azure Container Registry (i.e: shar01acrjwj7qfmkbx)',
      )}

      {formTextField(
        { ...imageNameCall },
        { marginBottom: '20px' },
        'Image Name',
        'Name of versioned container image',
      )}
      <Select
        {...aquaSecurityPolicyCall}
        sx={{
          backgroundColor: themeID === 'dark-theme' ? '#424242' : 'white',
          color: themeID === 'dark-theme' ? 'white' : 'black',
        }}
      >
        {securityPolicyArr.map(policy => (
          <MenuItem value={policy.value}>{policy.label}</MenuItem>
        ))}
      </Select>
      <FormHelperText>Security Policy</FormHelperText>

      <div style={{ marginBottom: '40px' }} />

      <Select
        {...exemptionCategoryCal}
        sx={{
          backgroundColor: themeID === 'dark-theme' ? '#424242' : 'white',
          color: themeID === 'dark-theme' ? 'white' : 'black',
        }}
      >
        {exemptionCategoryArr.map(exemption => (
          <MenuItem value={exemption.value}>{exemption.label}</MenuItem>
        ))}
      </Select>
      <FormHelperText>Exemption Category</FormHelperText>
      <div style={{ marginBottom: '20px' }} />

      {justificationField(justificationCall, { marginBottom: '20px' })}

      <div style={{ marginBottom: '40px' }} />

      {durationField(durationCall, { marginBottom: '20px' })}
    </FormControl>
  );
};

export const wizForm = (
  applicationNameCall:
    | UseFormRegisterReturn<'fieldArray.applicationName'>
    | UseFormRegisterReturn<`fieldArray.${number}.applicationName`>,
  containerRegistryNameCall:
    | UseFormRegisterReturn<'fieldArray.containerRegistryName'>
    | UseFormRegisterReturn<`fieldArray.${number}.containerRegistryName`>,
  imageNameCall:
    | UseFormRegisterReturn<'fieldArray.imageName'>
    | UseFormRegisterReturn<`fieldArray.${number}.imageName`>,
  wizSecurityPolicyCall:
    | UseFormRegisterReturn<'fieldArray.wizSecurityPolicy'>
    | UseFormRegisterReturn<`fieldArray.${number}.wizSecurityPolicy`>,
  otherSecurityPolicyCall:
    | UseFormRegisterReturn<'fieldArray.otherSecurityPolicy'>
    | UseFormRegisterReturn<`fieldArray.${number}.otherSecurityPolicy`>,
  exemptionCategoryCall:
    | UseFormRegisterReturn<'fieldArray.exemptionCategory'>
    | UseFormRegisterReturn<`fieldArray.${number}.exemptionCategory`>,
  riskLvlCall:
    | UseFormRegisterReturn<'fieldArray.riskLvl'>
    | UseFormRegisterReturn<`fieldArray.${number}.riskLvl`>,
  exemptionTypeCall:
    | UseFormRegisterReturn<'fieldArray.exemptionType'>
    | UseFormRegisterReturn<`fieldArray.${number}.exemptionType`>,
  justificationCall:
    | UseFormRegisterReturn<'fieldArray.justification'>
    | UseFormRegisterReturn<`fieldArray.${number}.justification`>,
  durationCall:
    | UseFormRegisterReturn<'fieldArray.duration'>
    | UseFormRegisterReturn<`fieldArray.${number}.duration`>,
  selectedSecurityPolicy: string | undefined,
  selectedExemptionType: string | undefined,
  themeID: string | undefined,
) => {
  const selectStyling = {
    backgroundColor: themeID === 'dark-theme' ? '#424242' : 'white',
    color: themeID === 'dark-theme' ? 'white' : 'black',
  };

  return (
    <FormControl>
      {formTextField(
        { ...applicationNameCall },
        { marginBottom: '20px' },
        'Name of the Application',
        'Name of Application (e.g mojito-backend)',
      )}

      {formTextField(
        { ...containerRegistryNameCall },
        { marginBottom: '20px' },
        'Name of Container Reigstry',
        'Name of Azure Container Registry (i.e: shar01acrjwj7qfmkbx)',
      )}

      {formTextField(
        { ...imageNameCall },
        { marginBottom: '20px' },
        'Image Name',
        'Name of versioned container image',
      )}

      <FormHelperText>Security Policy</FormHelperText>

      <Select {...wizSecurityPolicyCall} sx={selectStyling}>
        {wizSecurityPolicyGroups.map(group => [
          <ListSubheader key={group.section}>{group.section}</ListSubheader>,
          ...group.options.map(policy => (
            <MenuItem
              key={`${group.section}-${policy.value}`}
              value={policy.value}
            >
              {policy.label}
            </MenuItem>
          )),
        ])}
      </Select>

      {docsLinkHelperText('Refer to the ', 'Wiz section', ' for details')}

      {isOtherWizSecurityPolicy(selectedSecurityPolicy) &&
        formTextField(
          { ...otherSecurityPolicyCall },
          { marginBottom: '20px', marginTop: '10px' },
          'Policy Name',
          'Type the exact policy name',
        )}

      <div style={{ marginBottom: '20px' }} />

      <FormHelperText>Exception Category</FormHelperText>

      <Select {...exemptionCategoryCall} sx={selectStyling}>
        {exemptionCategoryArr.map(exemption => (
          <MenuItem key={exemption.value} value={exemption.value}>
            {exemption.label}
          </MenuItem>
        ))}
      </Select>

      {docsLinkHelperText(
        'Refer to ',
        'Appendix I',
        ' for Exception Request Categories',
      )}

      <div style={{ marginBottom: '20px' }} />

      <FormHelperText>Risk Level</FormHelperText>

      <Select {...riskLvlCall} sx={selectStyling}>
        {riskArr.map(risk => (
          <MenuItem key={risk.value} value={risk.value}>
            {risk.label}
          </MenuItem>
        ))}
      </Select>

      <div style={{ marginBottom: '20px' }} />

      <FormHelperText>Exception Type</FormHelperText>

      <Select {...exemptionTypeCall} sx={selectStyling}>
        {exemptionTypeArr.map(exemptionType => (
          <MenuItem key={exemptionType.value} value={exemptionType.value}>
            {exemptionType.label}
          </MenuItem>
        ))}
      </Select>

      <div style={{ marginBottom: '20px' }} />

      {justificationField(justificationCall, { marginBottom: '20px' })}

      <div style={{ marginBottom: '40px' }} />

      {selectedExemptionType === 'temporary' &&
        durationField(
          durationCall,
          { marginBottom: '20px' },
          { min: 0, max: 180 },
        )}
    </FormControl>
  );
};

export const securityExceptionValidator = (
  submissionData: any,
  validation: FieldValidation,
) => {
  if (submissionData.name === 'wiz') {
    if (submissionData.applicationName.length <= 0) {
      validation.addError('Application name cannot be empty');
    }

    if (submissionData.containerRegistryName.length <= 0) {
      validation.addError('Container Registry name cannot be empty');
    }

    if (submissionData.imageName.length <= 0) {
      validation.addError('Image name cannot be empty');
    }

    if (!submissionData.wizSecurityPolicy) {
      validation.addError(`Select from security policy dropdown.`);
    }

    if (
      isOtherWizSecurityPolicy(submissionData.wizSecurityPolicy) &&
      (submissionData.otherSecurityPolicy === undefined ||
        submissionData.otherSecurityPolicy.trim().length <= 0)
    ) {
      validation.addError(`Type the exact policy name.`);
    }

    if (submissionData.exemptionCategory === '') {
      validation.addError(`Select from exemption dropdown.`);
    }

    if (!submissionData.riskLvl) {
      validation.addError(`Select from risk level dropdown.`);
    }

    if (!submissionData.exemptionType) {
      validation.addError(`Select an Exception Type from dropdown`);
    }
  }

  if (submissionData.name === 'aqua') {
    if (submissionData.applicationName.length <= 0) {
      validation.addError('Application name cannot be empty');
    }

    if (submissionData.containerRegistryName.length <= 0) {
      validation.addError('Container Registry name cannot be empty');
    }

    if (submissionData.imageName.length <= 0) {
      validation.addError('Image name cannot be empty');
    }

    if (submissionData.aquaSecurityPolicy === '') {
      validation.addError(`Select from security policy dropdown.`);
    }

    if (submissionData.exemptionCategory === '') {
      validation.addError(`Select from exemption dropdown.`);
    }
  }

  if (submissionData.name === 'cse') {
    if (submissionData.subscriptionName.length <= 0) {
      validation.addError(`Subscription Name Required`);
    }

    if (submissionData.resourceGroup.length <= 0) {
      validation.addError(`Resource Group Required`);
    }

    if (submissionData.resourceName.length <= 0) {
      validation.addError(`Resource Name Required`);
    }

    if (submissionData.cloudSecurityPolicy.length <= 0) {
      validation.addError(`Cloud Security Policy Required`);
    }

    if (submissionData.exemptionCategory === '') {
      validation.addError(`Select an Exemption Category from dropdown`);
    }

    if (submissionData.exemptionType === '') {
      validation.addError(`Select from exemption dropdown.`);
    }
  }

  if (submissionData.name === 'snyk') {
    if (submissionData.snykOrgId.length <= 0) {
      validation.addError(`Snyk Org ID Required`);
    }

    if (submissionData.snykProjectId.length <= 0) {
      validation.addError(`Snyk Project ID Required`);
    }

    if (submissionData.linkToIssue.length <= 0) {
      validation.addError(`Link To Issue Required`);
    }

    if (submissionData.exemptionCategory === '') {
      validation.addError(`Select an Exemption Category from dropdown`);
    }

    if (submissionData.exemptionType === '') {
      validation.addError(`Select from exemption dropdown.`);
    }
  }

  if (submissionData.name === 'appsec-archsec') {
    if (submissionData.projectNameAndEnv.length <= 0) {
      validation.addError(`Project Name & Environment Required`);
    }

    if (submissionData.version.length <= 0) {
      validation.addError(`Version Required`);
    }

    if (submissionData.desc.length <= 0) {
      validation.addError(`Description of the Security Issue Required`);
    }

    if (submissionData.adoWorkNumber.length <= 0) {
      validation.addError(`ADO Work Item Number Required`);
    }

    if (submissionData.riskLvl === '') {
      validation.addError(`Select from risk level dropdown.`);
    }

    if (submissionData.exemptionCategory === '') {
      validation.addError(`Select an Exemption Category from dropdown`);
    }

    if (submissionData.exemptionType === '') {
      validation.addError(`Select from exemption dropdown.`);
    }
  }

  if (submissionData.justification.length <= 10) {
    validation.addError(
      'Justification reason cannot be empty and must be descriptive',
    );
  }

  /*
   Duration is only collected for a WIZ exception when the requester picks a
   temporary exception type, so it is only validated in that case.
  */
  const durationApplies =
    submissionData.name !== 'wiz' ||
    submissionData.exemptionType !== 'permanent';

  if (durationApplies) {
    if (submissionData.duration === '0' || submissionData.duration === '') {
      validation.addError('Enter a non zero number for duration. ');
    } else if (
      submissionData.name === 'wiz' &&
      Number(submissionData.duration) > 180
    ) {
      validation.addError('Duration cannot be greater than 180 days.');
    }
  }
};

export const SecurityExceptionFormUI = ({
  onChange,
  formContext,
}: FieldProps<string>) => {
  const { register, watch } = useForm();

  const themeApi = useApi(appThemeApiRef);

  const conditionalVar = formContext?.formData?.exception_type;

  if (
    formContext?.formData?.exception_type === 'Aqua Exception' &&
    watch().fieldArray !== undefined
  ) {
    watch().fieldArray.name = 'aqua';

    const payload = watch().fieldArray;

    onChange(payload);
  } else if (
    formContext?.formData?.exception_type === 'WIZ Exception' &&
    watch().fieldArray !== undefined
  ) {
    watch().fieldArray.name = 'wiz';

    const payload = watch().fieldArray;

    onChange(payload);
  } else if (
    formContext?.formData?.exception_type === 'Cloud Security Exception' &&
    watch().fieldArray !== undefined
  ) {
    watch().fieldArray.name = 'cse';

    const payload = watch().fieldArray;

    onChange(payload);
  } else if (
    formContext?.formData?.exception_type === 'Snyk Security Exception' &&
    watch().fieldArray !== undefined
  ) {
    watch().fieldArray.name = 'snyk';

    const payload = watch().fieldArray;
    onChange(payload);
  } else if (
    appSecArchSecExceptionTypes.includes(
      formContext?.formData?.exception_type,
    ) &&
    watch().fieldArray !== undefined
  ) {
    watch().fieldArray.name = 'appsec-archsec';

    const payload = watch().fieldArray;
    onChange(payload);
  } else {
    onChange({});
  }

  let formToRender = null;

  if (conditionalVar === 'Aqua Exception') {
    formToRender = aquaForm(
      register(`fieldArray.applicationName` as const),
      register(`fieldArray.containerRegistryName` as const),
      register(`fieldArray.imageName` as const),
      register(`fieldArray.aquaSecurityPolicy` as const),
      register(`fieldArray.exemptionCategory` as const),
      register(`fieldArray.justification` as const),
      register(`fieldArray.duration` as const),
      themeApi.getActiveThemeId(),
    );
  } else if (conditionalVar === 'WIZ Exception') {
    formToRender = wizForm(
      register(`fieldArray.applicationName` as const),
      register(`fieldArray.containerRegistryName` as const),
      register(`fieldArray.imageName` as const),
      register(`fieldArray.wizSecurityPolicy` as const),
      register(`fieldArray.otherSecurityPolicy` as const),
      register(`fieldArray.exemptionCategory` as const),
      register(`fieldArray.riskLvl` as const),
      register(`fieldArray.exemptionType` as const),
      register(`fieldArray.justification` as const),
      register(`fieldArray.duration` as const),
      watch(`fieldArray.wizSecurityPolicy`),
      watch(`fieldArray.exemptionType`),
      themeApi.getActiveThemeId(),
    );
  } else if (conditionalVar === 'Cloud Security Exception') {
    formToRender = cloudSecurityForm(
      register(`fieldArray.subscriptionName` as const),
      register(`fieldArray.resourceGroup` as const),
      register(`fieldArray.resourceName` as const),
      register(`fieldArray.cloudSecurityPolicy` as const),
      register(`fieldArray.exemptionCategory` as const),
      register(`fieldArray.exemptionType` as const),
      register(`fieldArray.justification` as const),
      register(`fieldArray.duration` as const),
      themeApi.getActiveThemeId(),
    );
  } else if (conditionalVar === 'Snyk Security Exception') {
    formToRender = snykForm(
      register(`fieldArray.snykOrgId` as const),
      register(`fieldArray.snykProjectId` as const),
      register(`fieldArray.linkToIssue` as const),
      register(`fieldArray.exemptionCategory` as const),
      register(`fieldArray.exemptionType` as const),
      register(`fieldArray.justification` as const),
      register(`fieldArray.duration` as const),
      themeApi.getActiveThemeId(),
    );
  } else if (appSecArchSecExceptionTypes.includes(conditionalVar)) {
    formToRender = appsec_archsec(
      register(`fieldArray.projectNameAndEnv` as const),
      register(`fieldArray.version` as const),
      register(`fieldArray.desc` as const),
      register(`fieldArray.adoWorkNumber` as const),
      register(`fieldArray.riskLvl` as const),
      register(`fieldArray.exemptionCategory` as const),
      register(`fieldArray.exemptionType` as const),
      register(`fieldArray.justification` as const),
      register(`fieldArray.duration` as const),
      themeApi.getActiveThemeId(),
    );
  }

  return <>{formToRender}</>;
};

export const securityExceptionFormValidation = (
  value:
    | wizExceptionType
    | aquaExceptionType
    | cloudSecurityExceptionType
    | snykExceptionType,
  validation: FieldValidation,
) => {
  if (JSON.stringify(value) !== '{}' && value !== undefined) {
    securityExceptionValidator(value, validation);
  } else {
    validation.addError('Please fill out the form before proceeding');
  }
};
